import React from 'react';
import { ShieldCheckIcon, XIcon, CheckIcon, CheckCircle2Icon, FileTextIcon, MapPinIcon, DatabaseIcon, BookOpenIcon } from 'lucide-react';
import { atnaHowCopy as c, chatbotPoints, atnaPoints, tameDemo, tameSources, type Bi, type SourceKey } from '../../../data/atnaHow';
type Pick = (b: Bi) => string;
const SOURCE_ICONS: Record<SourceKey, typeof FileTextIcon> = {
  bylaw: FileTextIcon,
  territory: MapPinIcon,
  internal: DatabaseIcon,
  official: BookOpenIcon
};
export function TrustStatement({
  pick
}: {
  pick: Pick;
}) {
  return <div className="h-full rounded-3xl bg-[#F1F0FB] px-7 md:px-8 py-6 md:py-7 flex items-center gap-5" data-id="element-1091" data-mp="1h8ek5ctj2wt7">
      <ShieldCheckIcon className="w-9 h-9 text-[#4F46E5] shrink-0 self-start mt-1" strokeWidth={1.4} aria-hidden="true" data-id="element-1092" data-mp="18a1gtg7niz6s" />
      <p className="font-serif text-[1.65rem] md:text-[1.9rem] leading-[1.12]" data-id="element-1093" data-mp="2xohava1cojm8">
        <span className="block sm:whitespace-nowrap text-[#121A3A]" data-id="element-1094" data-mp="32ntzo0bs80rg">{pick(c.trust.lead)}</span>
        {c.trust.accent.map(l => <span key={l.fr} className="block sm:whitespace-nowrap text-[#4F46E5]" data-id="element-1095" data-mp="348zz6uyo5kd8">{pick(l)}</span>)}
      </p>
    </div>;
}
function PointList({
  items,
  kind,
  pick
}: {
  items: Bi[];
  kind: 'no' | 'yes';
  pick: Pick;
}) {
  return <ul className="mt-2.5 space-y-1.5" data-id="element-1096" data-mp="3d4fyhri70rq7">
      {items.map(p => <li key={p.fr} className="flex items-start gap-2.5 font-sans text-[13px] leading-snug text-[#3A4260]" data-id="element-1097" data-mp="3921h7bu6a011">
          {kind === 'no' ? <span className="mt-[1px] w-4 h-4 rounded-full bg-[#FBD5D5] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-1098" data-mp="2ld0d20sdhb1k">
              <XIcon className="w-2.5 h-2.5 text-[#DC2626]" strokeWidth={3} data-id="element-1099" data-mp="2uc3yvcixc4jr" />
            </span> : <span className="mt-[1px] w-4 h-4 rounded-full bg-[#22C55E] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-1100" data-mp="2pbpmxje7cagg">
              <CheckIcon className="w-2.5 h-2.5 text-white" strokeWidth={3} data-id="element-1101" data-mp="9nppd3rbdyka" />
            </span>}
          <span data-id="element-1102" data-mp="4ow9j4ok1vgy">
            <span className="sr-only" data-id="element-1103" data-mp="2yerwr9g83l0d">{kind === 'no' ? pick({
            fr: 'Non : ',
            en: 'No: '
          }) : pick({
            fr: 'Oui : ',
            en: 'Yes: '
          })}</span>
            {pick(p)}
          </span>
        </li>)}
    </ul>;
}
export function Comparison({
  pick
}: {
  pick: Pick;
}) {
  return <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-9 lg:gap-5 h-full" data-id="element-1104" data-mp="37d3ioy659umh">
      <div className="rounded-2xl bg-[#FDF6F6] border border-[#F6E6E6] px-5 py-3.5" data-id="element-1105" data-mp="3cdht2mv7xgno">
        <h3 className="flex items-center gap-2 font-sans text-[14px] font-semibold text-[#121A3A]" data-id="element-1106" data-mp="2fpblbr302856">
          <span className="w-4 h-4 rounded-[5px] bg-[#EF4444] flex items-center justify-center" aria-hidden="true" data-id="element-1107" data-mp="2jq564bl5b6tu">
            <XIcon className="w-3 h-3 text-white" strokeWidth={3} data-id="element-1108" data-mp="2ondn48uk54ti" />
          </span>
          {pick(c.chatbotTitle)}
        </h3>
        <PointList items={chatbotPoints} kind="no" pick={pick} data-id="element-1109" data-mp="1w016sgezpt8n" />
      </div>

      <div className="rounded-2xl bg-[#F5F5FD] border border-[#E6E5F8] px-5 py-3.5" data-id="element-1110" data-mp="1vxyf7ry6vs1t">
        <h3 className="flex items-center gap-2 font-sans text-[14px] font-semibold text-[#4F46E5]" data-id="element-1111" data-mp="20xb2uewvx35g">
          <span className="w-4 h-4 rounded-full bg-[#4F46E5] flex items-center justify-center" aria-hidden="true" data-id="element-1112" data-mp="1tetlxnmoim2g">
            <CheckIcon className="w-3 h-3 text-white" strokeWidth={3} data-id="element-1113" data-mp="1oep67ztlhvda" />
          </span>
          {pick(c.atnaTitle)}
        </h3>
        <PointList items={atnaPoints} kind="yes" pick={pick} data-id="element-1114" data-mp="1fgd7tbmhltaa" />
      </div>

      <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-[#EEEFF7] border-4 border-white font-sans text-[13px] font-semibold text-[#5A6180] flex items-center justify-center" aria-hidden="true" data-id="element-1115" data-mp="1agqt0xr0g4zd">
        VS
      </span>
    </div>;
}
export function TamePanel({
  pick
}: {
  pick: Pick;
}) {
  const r = 46;
  const circ = 2 * Math.PI * r;
  const pct = tameDemo.confidence;
  return <div className="h-full rounded-2xl border border-[#E6E5F8] bg-[#FBFBFE] px-5 py-3.5 flex flex-col" data-id="element-1116" data-mp="roy1l7rlartr">
      <h3 className="font-sans text-[14px] font-semibold text-[#121A3A]" data-id="element-1117" data-mp="mroehyroas93">{pick(c.tameTitle)}</h3>
      <p className="font-sans text-[11.5px] text-[#6B7288] mt-0.5" data-id="element-1118" data-mp="2mupbw9y1xm78">{pick(c.tameCaption)}</p>
      <div className="mt-3 flex flex-col sm:flex-row xl:flex-col 2xl:flex-row items-center gap-4" data-id="element-1119" data-mp="2qtpp9cxrfwmu">
        <div className="relative w-[84px] h-[84px] shrink-0" role="img" aria-label={`${pick({
        fr: `${pct} %`,
        en: `${pct}%`
      })} — ${pick(c.tameCaption)}`} data-id="element-1120" data-mp="2d2t94ll0t7wo">
          <svg viewBox="0 0 110 110" className="w-full h-full -rotate-90" data-id="element-1121" data-mp="235ol0g7m72d8">
            <circle cx="55" cy="55" r={r} fill="none" stroke="#EEEDFB" strokeWidth="8" data-id="element-1122" data-mp="237j4rr75gofq" />
            <circle cx="55" cy="55" r={r} fill="none" stroke="#4F46E5" strokeWidth="8" strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={circ * (1 - pct / 100)} data-id="element-1123" data-mp="285t0q40p7t2e" />
          </svg>
          <span className="absolute inset-0 flex items-center justify-center font-sans text-[19px] font-semibold text-[#4F46E5]" data-id="element-1124" data-mp="3thumg7octw4t">
            {pick({
            fr: `${pct} %`,
            en: `${pct}%`
          })}
          </span>
        </div>
        <ul className="space-y-1.5 min-w-0" data-id="element-1125" data-mp="2hu2gw6bdmgn">
          {tameSources.map(s => {
          const Icon = SOURCE_ICONS[s.key];
          return <li key={s.key} className="flex items-center gap-2 font-sans text-[12.5px] text-[#2A3150]" data-id="element-1126" data-mp="ga07bm40o70s">
                <Icon className="w-4 h-4 text-[#4F46E5] shrink-0" strokeWidth={1.7} aria-hidden="true" data-id="element-1127" data-mp="bfvzz1qu9wz6" />
                <span className="whitespace-nowrap" data-id="element-1128" data-mp="q8pqdi7nfxim">{pick(s.label)}</span>
                <CheckCircle2Icon className="w-3.5 h-3.5 text-[#16A34A] shrink-0" strokeWidth={2} aria-label={pick({
              fr: 'vérifié',
              en: 'verified'
            })} data-id="element-1129" data-mp="l7a3dklwvgxl" />
              </li>;
        })}
        </ul>
      </div>
      <p className="mt-auto pt-3 flex items-center gap-1.5 font-sans text-[11.5px] font-medium text-[#2A3150]" data-id="element-1130" data-mp="v3v8s71ueu1y">
        <CheckCircle2Icon className="w-3.5 h-3.5 text-[#16A34A]" strokeWidth={2} aria-hidden="true" data-id="element-1131" data-mp="310r5eqvw3lma" />
        {pick(c.tameFooter)}
      </p>
    </div>;
}